/**
 * Apollo Client with BatchHttpLink for OneGraph Integration
 *
 * This client is configured to work with Indeed's OneGraph GraphQL API,
 * using the same endpoints and headers as smart-apply-webapp.
 *
 * Features:
 * - Automatic query batching to reduce HTTP round trips
 * - OneGraph-specific endpoints and authentication
 * - Smart caching for Indeed's schema types
 * - Environment-based configuration
 * - Mock support for local development only
 * - SSR compatibility
 *
 * Environment Configuration:
 * - Config files: `config/*.properties` → `graphqlEndpoint` and `oneGraphApiKey`
 * - NODE_ENV: Determines mock vs real endpoints
 */

import {
    ApolloClient,
    InMemoryCache,
    from,
    NormalizedCacheObject,
    type ApolloCache,
    type TypePolicies,
} from '@apollo/client/core';
import type { Modifiers } from '@apollo/client/cache';
import { relayStylePagination } from '@apollo/client/utilities';
import { BatchHttpLink } from '@apollo/client/link/batch-http';
import { onError } from '@apollo/client/link/error';
import { createMockLink } from './mockLink';
import { CTK_HEADER } from './headers';
import { EventLogger } from '../eventLogger/eventLogger.client';
import { AvroOptional } from '@indeed/signals-schema/com/indeed/signals/schema/indeedapply/AutoApplySetupButtonClickEvent';

// SSR-specific options for Apollo client creation
interface SSROptions {
    headers?: Record<string, string>;
    cookies?: string;
    /** Site country code (geoCountry) - Required */
    countryCode: string;
    /** Locale string (e.g., 'en-US', 'ja-JP') - Required */
    locale: string;
    /** GraphQL endpoint from config files - Optional */
    graphqlEndpoint?: string;
    /** OneGraph API key from config files - Required for production */
    oneGraphApiKey?: string;
    /** Application name from config files - Used for OneGraph Router migration (IAFE-430) */
    appName: string;
}

// Import DevTools debug utilities in development
if (process.env.NODE_ENV === 'development') {
    void import('./devtools-debug');
}

// Extend window interface to include Apollo state
declare global {
    interface Window {
        __APOLLO_STATE__?: NormalizedCacheObject;
    }
}

// Environment configuration
// Note: process.env is replaced at build time by webpack.DefinePlugin
const isDevelopment = process.env.NODE_ENV === 'development';
const isLocalDev = isDevelopment && process.env.REACT_APP_USE_MOCK_DATA === 'true';

// Error handling link
export const createErrorLink = (eventLogger?: EventLogger) => {
    return onError(({ graphQLErrors, networkError, operation }) => {
        if (!eventLogger) return;

        const currentPage = eventLogger.getPathname();
        const isReviewFlow =
            currentPage === '/auto-apply' || currentPage.startsWith('/auto-apply/');
        const logErrorFn = isReviewFlow ? 'logReviewError' : 'logAutoApplySetupErrorEvent';

        if (graphQLErrors) {
            graphQLErrors.forEach((graphQLError) => {
                eventLogger[logErrorFn]?.({
                    currentPage,
                    errorMessage: AvroOptional.string(graphQLError.message),
                    errorType: AvroOptional.string('graphql_error'),
                    graphqlOperation: AvroOptional.string(operation.operationName),
                });
            });
        }

        if (networkError) {
            eventLogger[logErrorFn]?.({
                currentPage,
                errorMessage: AvroOptional.string(networkError.message),
                errorType: AvroOptional.string('network_error'),
                graphqlOperation: AvroOptional.string(operation.operationName),
                errorStack: AvroOptional.string(networkError.stack),
            });
        }
    });
};

// Get CTK from browser cookies (following smart-apply-webapp pattern)
const getCtk = (ssrCookies?: string): string => {
    // `GENERATE` option can be used only on the user's browser.
    // If it's set, OneGraph generates a new CTK and return in indeed-ctk-generated header.
    // While SSR, we cannot set this value in the user's browser.
    const fallbackOption = ssrCookies ? '' : 'GENERATE';
    try {
        let cookies: Record<string, string> = {};

        if (typeof document !== 'undefined') {
            // Browser: Parse cookies from document.cookie
            cookies = parseCookies(document.cookie);
        } else if (ssrCookies) {
            // Server-side: Parse cookies from HTTP request
            console.log('[SSR]: Extracting CTK from server-side cookies');
            cookies = parseCookies(ssrCookies);
        } else {
            // Server-side with no cookies
            console.log('[SSR]: No cookies available, using fallback');
            return '';
        }

        const ctk = cookies.CTK;
        if (ctk) {
            console.log('[CTK]: Successfully extracted CTK from cookies');
            return ctk;
        } else {
            console.log('[CTK]: No CTK found in cookies, using fallback');
            return fallbackOption;
        }
    } catch (error) {
        console.log('[CTK]: Error parsing cookies, using fallback:', error);
        return fallbackOption; // Fallback on any parsing error
    }
};

// Parse cookies helper function (works for both browser and SSR)
//
// Must never throw. Both callers swallow failures and fall back, so a single bad
// cookie silently degrades every request: getCtk drops to 'GENERATE', minting a new
// CTK per request instead of reading the real one, which breaks attribution.
const parseCookies = (cookieString: string): Record<string, string> => {
    return cookieString.split(';').reduce(
        (acc, cookie) => {
            // indexOf/slice, not `const [key, value] = cookie.trim().split('=')`:
            // the mangler collapses that to a self-referential
            // `var [t, n] = t.trim().split('=')`, which throws on iOS 16/17 (IAFE-2851).
            // Splitting on the first '=' only also keeps values that contain '=',
            // which split('=') silently truncated.
            const trimmed = cookie.trim();
            const separator = trimmed.indexOf('=');
            if (separator <= 0) {
                return acc;
            }
            const key = trimmed.slice(0, separator);
            const value = trimmed.slice(separator + 1);
            if (!value) {
                return acc;
            }
            try {
                acc[key] = decodeURIComponent(value);
            } catch {
                // Malformed percent-encoding in one cookie must not discard the rest.
                acc[key] = value;
            }
            return acc;
        },
        {} as Record<string, string>,
    );
};

// Get headers for OneGraph API (matching smart-apply-webapp headers)
const getOneGraphHeaders = (
    ssrCookies: string | undefined,
    countryCode: string,
    locale: string,
    appName: string,
    oneGraphApiKey?: string,
) => {
    const headers: Record<string, string> = {
        'Content-Type': 'application/json',
    };

    // Client sub-app header for OneGraph Router migration (IAFE-430)
    // Required for traffic routing to OneGraph Router and client identification
    headers['indeed-client-sub-app'] = appName;

    // OneGraph API Key from config files
    // This is a public API key that gets passed to the browser for client-side GraphQL
    if (oneGraphApiKey) {
        headers['Indeed-API-Key'] = oneGraphApiKey;
        console.log('[OneGraph]: API key configured from config files');
    } else {
        console.warn('[OneGraph]: No API key configured - requests may fail in production');
    }

    // Use standard CTK handling (following smart-apply-webapp pattern)
    headers[CTK_HEADER] = getCtk(ssrCookies);

    // Get country code and locale from parameters
    headers['indeed-co'] = countryCode;
    headers['indeed-locale'] = locale;

    // Global proctor groups (following smart-apply-webapp pattern)
    // This enables access to experimental features like shorturl conversion
    try {
        let cookies: Record<string, string> = {};

        if (typeof document !== 'undefined') {
            // Browser: Parse cookies from document.cookie
            cookies = parseCookies(document.cookie);
        } else if (ssrCookies) {
            // SSR: Parse cookies from HTTP request
            cookies = parseCookies(ssrCookies);
        }

        const prforceGroups = cookies.prforceGroups;
        if (prforceGroups) {
            headers['indeed-prforce-groups'] = prforceGroups;
        } else {
            // Fallback to test group for development/testing when no cookies
            headers['indeed-prforce-groups'] = 'ia_applylink_short_url_tst1';
        }
    } catch {
        // Fallback to test group on any parsing error
        headers['indeed-prforce-groups'] = 'ia_applylink_short_url_tst1';
    }

    return headers;
};

// Create Apollo link chain based on environment
const createApolloLink = (ssrOptions: SSROptions, eventLogger?: EventLogger) => {
    console.log('🔍 Apollo Client Config:', {
        isDevelopment,
        hasApiKey: !!ssrOptions.oneGraphApiKey,
        useMockData: process.env.REACT_APP_USE_MOCK_DATA,
        isLocalDev,
        endpoint: ssrOptions.graphqlEndpoint,
    });

    if (isLocalDev) {
        console.log('🧪 Using Mock Apollo Link for local development');
        // Using Mock Apollo Link for local development
        // Note: graphqlEndpoint not required in mock mode
        return from([createErrorLink(), createMockLink()]);
    } else {
        // Fail fast if graphqlEndpoint is missing - prevents accidentally using QA endpoint in production
        // Only check when actually using real endpoint (not in mock mode)
        if (!ssrOptions.graphqlEndpoint) {
            const errorMessage =
                'GraphQL endpoint is missing. ' +
                'Expected graphqlEndpoint from config files (config/*.properties). ' +
                'Check that config files are properly loaded and graphqlEndpoint property is set.';

            console.error('❌ Apollo Client Configuration Error:', errorMessage);
            console.error('Environment:', {
                isDevelopment,
                isLocalDev,
                hasApiKey: !!ssrOptions.oneGraphApiKey,
                useMockData: process.env.REACT_APP_USE_MOCK_DATA,
            });

            throw new Error(errorMessage);
        }

        const endpoint = ssrOptions.graphqlEndpoint;
        console.log('🌐 Using BatchHttpLink for OneGraph endpoint:', endpoint);
        // Using BatchHttpLink for OneGraph production endpoint

        const baseHeaders = getOneGraphHeaders(
            ssrOptions.cookies,
            ssrOptions.countryCode,
            ssrOptions.locale,
            ssrOptions.appName,
            ssrOptions.oneGraphApiKey,
        );
        const ssrHeaders = ssrOptions.headers || {};

        // For SSR, include cookies in headers if provided
        const allHeaders = {
            ...baseHeaders,
            ...ssrHeaders,
            // Add cookie header if provided (SSR context)
            ...(ssrOptions.cookies && { Cookie: ssrOptions.cookies }),
        };

        console.log('🍪 Apollo Client Headers:', {
            isSSR: typeof window === 'undefined',
            hasCookies: !!ssrOptions.cookies,
            hasSSRHeaders: !!ssrOptions.headers,
            allHeaderKeys: Object.keys(allHeaders),
        });

        // Batch HTTP Link configuration for OneGraph
        const batchHttpLink = new BatchHttpLink({
            uri: endpoint,

            // Batch configuration (optimized for Indeed's use case)
            batchMax: 10, // Maximum number of operations to batch together
            batchInterval: 20, // Wait time in milliseconds before sending the batch

            // OneGraph-specific request configuration with SSR headers
            headers: allHeaders,

            // Credentials for cross-origin requests (required for Indeed APIs)
            credentials: 'include',

            // Custom fetch options for OneGraph
            fetchOptions: {
                mode: 'cors',
            },
        });

        return from([createErrorLink(eventLogger), batchHttpLink]);
    }
};

// ====================================================================
// 🎯 APOLLO CACHE CONFIGURATION - SIMPLIFIED
// ====================================================================
//
// Philosophy: Explicit is Better Than Implicit
// ---------------------------------------------
// - Let Apollo REPLACE cache entries completely (no auto-merge)
// - Use explicit cache.modify() when you need partial updates
// - This prevents bugs from auto-merge not respecting server intent
//
// Configuration Elements:
// ----------------------
// 1. keyFields: How Apollo identifies unique cache entries
//    - ['id']: Use 'id' field for normalization
//    - ['key']: Use 'key' field instead of 'id'
//    - []: Singleton (no unique identifier needed)
//
// 2. keyArgs: Cache separation for parameterized queries
//    - ['jobId']: Different jobIds get separate cache entries
//    - false: Single shared cache entry
//
// Why No Merge Functions?
// -----------------------
// Auto-merge is error-prone:
//   Server returns: { field: null }  (intentionally clearing)
//   Auto-merge: {...existing, field: null} → OLD VALUE KEPT! ❌
//   No merge: { field: null } → CLEARED as intended ✅
//
// For partial updates, use explicit cache.modify():
//   apolloClient.cache.modify({
//       fields: { field(existing) { return newValue; } }
//   });
//
// ====================================================================

export const cacheTypePolicies: TypePolicies = {
    // SmartApplyApplicationState caching by ID
    // No merge functions - let Apollo replace fields completely to respect server intent
    // Use explicit cache.modify() or updateSmartApplyContactInfoCache() for partial updates
    SmartApplyApplicationState: {
        keyFields: ['id'],
    },

    // JobSeekerProfile caching (single profile per user)
    JobSeekerProfile: {
        keyFields: [], // Single profile per user - no specific key fields needed
    },

    // AutoApplyInfoPayload caching (single info per user)
    AutoApplyInfoPayload: {
        keyFields: [], // Single auto apply info per user - no specific key fields needed
    },

    /* Not normalized: the success page and the draft-list query call this field with
       different filters, so a shared entry lets one overwrite the other's drafts. */
    FindReviewBeforeSubmitDraftsPayload: {
        keyFields: false,
    },

    // Job caching by job key
    Job: {
        keyFields: ['key'],
    },

    // DraftManagementApplyableJob normalised by jobId so partial writes
    // from different queries (e.g. FindApplicationTabData) merge into the
    // same cache entry rather than replacing it entirely.
    DraftManagementApplyableJob: {
        keyFields: ['jobId'],
    },

    // AutoApplyDraft caching by applicationDraftId
    // Ensures proper normalization for draft validation on index page
    AutoApplyDraft: {
        keyFields: ['applicationDraftId'],
    },

    // AutoApplyEligibleQuestion: disable normalization.
    // The API returns empty `id` for all questions, which causes Apollo
    // to collapse them into a single cache entry. Using `keyFields: false`
    // keeps each question embedded in its parent (edge node) instead.
    AutoApplyEligibleQuestion: {
        keyFields: false,
    },

    // ScreenerQuestionContextQuestion: disable normalization for the same reason.
    // Screener question ids are NOT unique — the same id (e.g. 'certification') can appear across
    // different drafts and even repeat within one draft — so normalizing by id would collapse
    // distinct questions into one cache entry (one draft's prefill overwriting another's). This is
    // why QuestionsPage reads its screener query with `no-cache`; `keyFields: false` instead lets the
    // read-only prefill query (useShowAiAutofillDisclaimer) stay `cache-first` safely by keeping each
    // question embedded in its parent.
    ScreenerQuestionContextQuestion: {
        keyFields: false,
    },

    // Query-level caching policies
    Query: {
        fields: {
            // Cache jobData queries by jobKeys parameter
            jobData: {
                keyArgs: ['jobKeys'],
            },

            // Cache node queries by ID
            node: {
                keyArgs: ['id'],
            },

            // JobSeekerProfile queries (single profile per user)
            jobSeekerProfile: {
                keyArgs: false, // Single profile per user - no key args needed
            },

            // AutoApplyInfo queries (single info per user)
            autoApplyInfo: {
                keyArgs: false,
            },

            // Relay-style cursor pagination for past answers.
            // Key by shouldIncludeHostedSQ so hosted (IQv2 + 3P SQ) and
            // non-hosted (3P SQ only) result sets are cached separately and
            // never merged across the ia_afm_iqv2_prefill_tst proctor decision.
            autoApplyEligiblePastAnswers: relayStylePagination(['shouldIncludeHostedSQ']),

            // DQ answers use the same cursor pagination contract, but come from the
            // demographic questions API and include account-level consent.
            eligiblePastDQAnswers: relayStylePagination(),

            // ApplyParameters queries - cache by job ID
            // All queries request the same fields (jobDetails.standardFields)
            // No merge needed - Apollo replaces completely
            applyParameters: {
                keyArgs: ['indeedApplyableJobId'],
            },
        },
    },
};

// Factory function to create a new Apollo client instance
// This is important for SSR to avoid data leakage between requests
export const createApolloClient = (
    initialState: NormalizedCacheObject | undefined,
    ssrOptions: SSROptions,
    eventLogger?: EventLogger,
): ApolloClient<NormalizedCacheObject> => {
    const cache = new InMemoryCache({ typePolicies: cacheTypePolicies });

    // Restore cache from initial state if provided (SSR hydration)
    if (initialState) {
        cache.restore(initialState);
    }

    const client = new ApolloClient({
        link: createApolloLink(ssrOptions, eventLogger),
        cache,
        // resolvers: [smartApplyStateCacheResolvers],

        // Development tools
        connectToDevTools: isDevelopment,

        // Default options for queries
        defaultOptions: {
            watchQuery: {
                errorPolicy: 'all',
                notifyOnNetworkStatusChange: true,
            },
            query: {
                errorPolicy: 'all',
            },
            mutate: {
                errorPolicy: 'all',
            },
        },

        // For SSR compatibility
        ssrMode: typeof window === 'undefined',
        assumeImmutableResults: true,
    });

    // Initialize localStorage GraphQL database for local development
    if (isDevelopment && typeof window !== 'undefined') {
        // Database is already initialized as singleton
        // localStorage GraphQL database enabled for local development
    }

    return client;
};

/**
 * Modifies fields on the normalized AutoApplyInfoPayload singleton in the Apollo cache.
 *
 * AutoApplyInfoPayload uses `keyFields: []` (singleton), so it's stored at
 * `AutoApplyInfoPayload:{}` — not inline on ROOT_QUERY. Always use this helper
 * instead of raw `cache.modify` to avoid accidentally targeting ROOT_QUERY.
 */
export const modifyAutoApplyInfo = <T extends object>(cache: ApolloCache<T>, fields: Modifiers) => {
    const id = cache.identify({ __typename: 'AutoApplyInfoPayload' });
    if (!id) {
        console.warn(
            'modifyAutoApplyInfo: AutoApplyInfoPayload not found in cache — skipping modify. ' +
                'This can happen if the entity has not been written to the cache yet.',
        );
        return false;
    }
    return cache.modify({ id, fields });
};

// Named exports for convenience
export { getOneGraphHeaders };

export default createApolloClient;
